import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  Info as InfoIcon,
  PowerSettingsNew as PowerIcon,
  Chat as ChatIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  AccessTime as PendingIcon
} from '@mui/icons-material';
import { useAuthToken } from "../TokenContext";
import { useSelector } from "react-redux";
import CreateAgent from './CreateAgent';
import { useTheme } from '@mui/material';
import AgentPerformanceMetrics from './Agentperformancemetrics';


const AGENT_STATUS = {
  provisioning: { label: 'Provisioning', color: 'warning', icon: <SettingsIcon fontSize="small" /> },
  active: { label: 'Active', color: 'success', icon: <PowerIcon fontSize="small" /> },
  stopped: { label: 'Stopped', color: 'default', icon: <StopIcon fontSize="small" /> },
  error: { label: 'Error', color: 'error', icon: <InfoIcon fontSize="small" /> }
};

const AGENT_TYPES = {
  product_owner: 'Product Owner',
  scrum_master: 'Scrum Master',
  coder: 'Coder',
  tester: 'Tester',
  designer: 'Designer',
  devops: 'DevOps',
  analyst: 'Analyst'
};

const APPROVAL_STATUS = {
  pending: { label: 'Pending', color: 'warning', icon: <PendingIcon fontSize="small" /> },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Retry', color: 'error' }
};

const AgentsList = ({ collapsed }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const { user } = useSelector(state => state.auth);
  const token = useAuthToken();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  // Main tab state (Agents vs Approvals)
  const [mainTab, setMainTab] = useState(0);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Execution Analysis States
  const [executionOpen, setExecutionOpen] = useState(false);
  const [executionAgent, setExecutionAgent] = useState(null);
  const [executionData, setExecutionData] = useState(null);
  const [executionLoading, setExecutionLoading] = useState(false);
  const [executionError, setExecutionError] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editAgent, setEditAgent] = useState(null);

  // Approvals States
  const [approvals, setApprovals] = useState([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [approvalStatusFilter, setApprovalStatusFilter] = useState('pending');
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [approvalDetailsOpen, setApprovalDetailsOpen] = useState(false);
  const [approvalActionLoading, setApprovalActionLoading] = useState(false);
 const [feedback, setFeedback] = useState('');
  const handleEditAgent = (agent) => {
    setEditAgent(agent);
    setEditOpen(true);
  };

  const handleEditSuccess = (updatedAgent) => {
    setEditOpen(false);
    setEditAgent(null);
    fetchAgents();
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditAgent(null);
  };

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    stopped: 0,
    provisioning: 0,
    error: 0
  });

  const [approvalStats, setApprovalStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0
  });

  useEffect(() => {
    if (user?.data?.[0]?.client_code && token) {
      fetchAgents();
      fetchApprovals();
    }
  }, [user, token, page, rowsPerPage, statusFilter]);

  useEffect(() => {
    if (mainTab === 1 && user?.data?.[0]?.client_code && token) {
      fetchApprovals();
    }
  }, [mainTab, approvalStatusFilter]);

useEffect(() => {
  let pollInterval;
  
  if (mainTab === 1) {
    // Poll every 10 seconds when on approvals tab
    pollInterval = setInterval(() => {
      fetchApprovals();
    }, 10000);
  }
  
  return () => {
    if (pollInterval) {
      clearInterval(pollInterval);
    }
  };
}, [mainTab]);

  
  const fetchAgents = async () => {
    if (!user?.data?.[0]?.client_code || !token) {
      console.log('Missing client_code or token');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const offset = page * rowsPerPage;
      
      console.log('Fetching agents with params:', {
        org_id: user.data[0].client_code,
        status: statusFilter || undefined,
        limit: rowsPerPage,
        offset: offset
      });

      const response = await axios.post(
        process.env.REACT_APP_CLIENT_LISTAGENTHANDLER,
        {
          env: 'prod',
          org_id: user.data[0].client_code,
          status: statusFilter || undefined,
          limit: rowsPerPage,
          offset: offset
        },
        { headers: { Authorization: token } }
      );

      console.log('Agents list API response:', response.data);

      if (response.data?.data?.agents) {
        setAgents(response.data.data.agents);
        setTotalCount(response.data.data.count || response.data.data.agents.length);
        
        // Calculate stats
        const agentStats = response.data.data.agents.reduce((acc, agent) => {
          acc.total++;
          acc[agent.status] = (acc[agent.status] || 0) + 1;
          return acc;
        }, { total: 0, active: 0, stopped: 0, provisioning: 0, error: 0 });
        
        setStats(agentStats);
      } else {
        setAgents([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      console.error('Error details:', err.response?.data);
      setError(err.response?.data?.data || err.message || 'Failed to load agents');
      
      // Mock data for testing - remove this in production
      console.log('Using mock data for testing');
      setAgents([
        {
          id: 1,
          name: 'Sprint Planning Bot',
          agent_type: 'scrum_master',
          status: 'active',
          pod_id: 'pod-123',
          pod_name: 'Development Team',
          approver_id: 'approver-1',
          approver_name: 'John Doe',
          trigger_type: 'scheduled',
          trigger_config: '0 9 * * 1',
          permissions: [
            { system: 'jira', perms: ['read', 'write'] },
            { system: 'slack', perms: ['read'] }
          ],
          created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          last_run_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          metadata: { description: 'Automates sprint planning activities' }
        },
        {
          id: 2,
          name: 'Code Review Assistant',
          agent_type: 'coder',
          status: 'active',
          pod_id: 'pod-124',
          pod_name: 'Backend Team',
          approver_id: 'approver-2',
          approver_name: 'Jane Smith',
          trigger_type: 'github_pr',
          trigger_config: 'org/repo',
          permissions: [
            { system: 'github', perms: ['read', 'write'] },
            { system: 'slack', perms: ['write'] }
          ],
          created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
          last_run_at: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
          metadata: { description: 'Reviews pull requests and suggests improvements' }
        },
        {
          id: 3,
          name: 'Test Automation Bot',
          agent_type: 'tester',
          status: 'stopped',
          pod_id: 'pod-125',
          pod_name: 'QA Team',
          approver_id: 'approver-3',
          approver_name: 'Bob Wilson',
          trigger_type: 'task_completion',
          trigger_config: 'JIRA-*',
          permissions: [
            { system: 'jira', perms: ['read'] },
            { system: 'database', perms: ['read'] },
            { system: 'aws', perms: ['read', 'list'] }
          ],
          created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          metadata: { description: 'Runs automated tests on completed tasks' }
        },
        {
          id: 4,
          name: 'Deployment Manager',
          agent_type: 'devops',
          status: 'provisioning',
          pod_id: 'pod-126',
          pod_name: 'DevOps Team',
          approver_id: 'approver-4',
          approver_name: 'Alice Cooper',
          trigger_type: 'manual',
          trigger_config: '',
          permissions: [
            { system: 'aws', perms: ['read', 'write', 'admin'] },
            { system: 'github', perms: ['read'] }
          ],
          created_at: new Date().toISOString(),
          metadata: { description: 'Manages deployment pipelines' }
        }
      ]);
      setTotalCount(4);
      setStats({ total: 4, active: 2, stopped: 1, provisioning: 1, error: 0 });
    } finally {
      setLoading(false);
    }
  };

  const parseEntityData = (entityDataString) => {
    try {
      // Extract summary from entity_data string
      const lines = entityDataString.split('\\n');
      let summary = '';
      
      // Try to find a meaningful summary line
      for (const line of lines) {
        if (line.includes('Summary:') || line.includes('Issue Summary:')) {
          summary = line.replace(/Summary:/gi, '').trim();
          break;
        }
      }
      
      // If no summary found, create a brief one from first few words
      if (!summary) {
        summary = entityDataString.substring(0, 150).replace(/"/g, '').trim() + '...';
      }
      
      return summary;
    } catch (err) {
      return 'Task details available';
    }
  };

 const fetchApprovals = async () => {
  if (!token) {
    console.log('Missing token');
    return;
  }

  setApprovalsLoading(true);
  setError('');
  
  try {
    console.log('Fetching approvals for manager:', user.data[0].emp_id);

    // Fetch from API
    let apiApprovals = [];
    try {
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_PODAUTOMATIONAGENTFEEDBACK,
        {
          manager_id: user.data[0].emp_id
        },
        { headers: { Authorization: token } }
      );

      console.log('Approvals API response:', response.data);

      if (response.data?.body && Array.isArray(response.data.body)) {
        apiApprovals = response.data.body
          .filter(item => {
            if (approvalStatusFilter === 'approved') return item.status === 'approved' || item.approved_by;
            if (approvalStatusFilter === 'rejected') return item.status === 'rejected' || item.feedback_comment;
            if (approvalStatusFilter === 'pending') return item.status === 'pending' && !item.approved_by;
            return true;
          })
          .map(item => ({
            id: item.id,
            manager_id: item.manager_id,
            agent_action: item.agent_action,
            task_type: item.agent_action,
            task_summary: parseEntityData(item.entity_data),
            entity_data: item.entity_data,
            status: item.approved_by ? 'approved' : (item.feedback_comment ? 'rejected' : 'pending'),
            feedback_comment: item.feedback_comment,
            approved_by: item.approved_by,
            created_at: item.created_at,
            executed_at: item.executed_at,
            jira_output: item.jira_output,
            agent_name: item.agent_action?.replace(/_/g, ' ') || 'Agent',
            agent_type: 'automation_agent',
            triggered_by: 'Automated',
            source: 'api'
          }));
      }
    } catch (apiErr) {
      console.error('API error (continuing with localStorage):', apiErr);
    }

    // Fetch mock approvals from localStorage
    const mockApprovals = JSON.parse(localStorage.getItem('mock_approvals') || '[]');
    console.log('Mock approvals from localStorage:', mockApprovals);

    const formattedMockApprovals = mockApprovals
      .filter(item => {
        // Filter by status
        if (approvalStatusFilter === 'approved') return item.status === 'approved';
        if (approvalStatusFilter === 'rejected') return item.status === 'rejected';
        if (approvalStatusFilter === 'pending') return item.status === 'pending';
        return true;
      })
      .map(item => {
        // Parse entity_data
        let entityData = item.entity_data;
        if (typeof entityData === 'string') {
          try {
            entityData = JSON.parse(entityData);
          } catch (e) {
            entityData = {};
          }
        }

        return {
          id: item.id,
          manager_id: item.manager_id,
          agent_action: item.agent_action,
          task_type: item.task_type,
          task_summary: item.task_summary,
          entity_data: typeof item.entity_data === 'string' ? item.entity_data : JSON.stringify(item.entity_data, null, 2),
          status: item.status,
          feedback_comment: item.feedback_comment,
          approved_by: item.approved_by,
          created_at: item.created_at,
          executed_at: item.executed_at,
          jira_output: item.jira_output,
          agent_name: entityData.agent_name || 'AI Agent',
          agent_type: 'coder',
          triggered_by: 'Kanban Assignment',
          source: 'mock'
        };
      });

    // Combine API and mock approvals
    const allApprovals = [...apiApprovals, ...formattedMockApprovals];
    
    // Sort by created_at (newest first)
    allApprovals.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    setApprovals(allApprovals);
    
    // Calculate approval stats from all data
    const allApprovalsForStats = [
      ...(apiApprovals.length > 0 ? apiApprovals : []),
      ...mockApprovals
    ];
    
    const stats = {
      pending: allApprovalsForStats.filter(a => 
        a.status === 'pending' || (!a.approved_by && !a.feedback_comment)
      ).length,
      approved: allApprovalsForStats.filter(a => 
        a.status === 'approved' || a.approved_by
      ).length,
      rejected: allApprovalsForStats.filter(a => 
        a.status === 'rejected' || (a.feedback_comment && !a.approved_by)
      ).length
    };
    
    setApprovalStats(stats);
    setUnreadNotifications(stats.pending);

  } catch (err) {
    console.error('Error fetching approvals:', err);
    
    // Even if API fails, try to load mock approvals
    const mockApprovals = JSON.parse(localStorage.getItem('mock_approvals') || '[]');
    
    const formattedMockApprovals = mockApprovals
      .filter(item => {
        if (approvalStatusFilter === 'approved') return item.status === 'approved';
        if (approvalStatusFilter === 'rejected') return item.status === 'rejected';
        if (approvalStatusFilter === 'pending') return item.status === 'pending';
        return true;
      })
      .map(item => {
        let entityData = item.entity_data;
        if (typeof entityData === 'string') {
          try {
            entityData = JSON.parse(entityData);
          } catch (e) {
            entityData = {};
          }
        }

        return {
          id: item.id,
          manager_id: item.manager_id,
          agent_action: item.agent_action,
          task_type: item.task_type,
          task_summary: item.task_summary,
          entity_data: typeof item.entity_data === 'string' ? item.entity_data : JSON.stringify(item.entity_data, null, 2),
          status: item.status,
          created_at: item.created_at,
          jira_output: item.jira_output,
          agent_name: entityData.agent_name || 'AI Agent',
          agent_type: 'coder',
          triggered_by: 'Kanban Assignment',
          source: 'mock'
        };
      });
    
    setApprovals(formattedMockApprovals);
    
    const stats = {
      pending: mockApprovals.filter(a => a.status === 'pending').length,
      approved: mockApprovals.filter(a => a.status === 'approved').length,
      rejected: mockApprovals.filter(a => a.status === 'rejected').length
    };
    
    setApprovalStats(stats);
    setUnreadNotifications(stats.pending);
    
    // Don't show error if we have mock data
    if (formattedMockApprovals.length === 0) {
      setError(err.response?.data?.message || err.message || 'Failed to load approvals');
    }
  } finally {
    setApprovalsLoading(false);
  }
};


 const handleApproveTask = async (approval) => {
  if (!feedback.trim()) {
    setError('Please provide feedback before approving');
    return;
  }

  setApprovalActionLoading(true);
  try {
    console.log('Approving task:', approval);
    
    // If this is a mock approval (from localStorage), update it there
    if (approval.source === 'mock') {
      const mockApprovals = JSON.parse(localStorage.getItem('mock_approvals') || '[]');
      const updatedApprovals = mockApprovals.map(item => {
        if (item.id === approval.id) {
          return {
            ...item,
            status: 'approved',
            approved_by: user.data[0].emp_id,
            feedback_comment: feedback,
            executed_at: new Date().toISOString()
          };
        }
        return item;
      });
      localStorage.setItem('mock_approvals', JSON.stringify(updatedApprovals));
      
      console.log('Mock approval approved successfully');
    } else {
      // API call for real approvals
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_APPROVEACTIONFEEDBACK,
        {
          manager_id: user.data[0].emp_id,
          action_id: approval.id,
          feedback: feedback
        },
        { headers: { Authorization: token } }
      );

      console.log('Task approved successfully:', response.data);
    }
    
    // Refresh approvals
    fetchApprovals();
    setApprovalDetailsOpen(false);
    setSelectedApproval(null);
    setFeedback('');
    
  } catch (err) {
    console.error('Error approving task:', err);
    setError(err.response?.data?.message || 'Failed to approve task');
  } finally {
    setApprovalActionLoading(false);
  }
};


// Line 526 - Update handleRejectTask to use feedback
const handleRejectTask = async (approval) => {
  if (!feedback.trim()) {
    setError('Please provide feedback before rejecting');
    return;
  }

  setApprovalActionLoading(true);
  try {
    console.log('Rejecting task:', approval);
    
    // If this is a mock approval (from localStorage), update it there
    if (approval.source === 'mock') {
      const mockApprovals = JSON.parse(localStorage.getItem('mock_approvals') || '[]');
      const updatedApprovals = mockApprovals.map(item => {
        if (item.id === approval.id) {
          return {
            ...item,
            status: 'rejected',
            feedback_comment: feedback,
            executed_at: new Date().toISOString()
          };
        }
        return item;
      });
      localStorage.setItem('mock_approvals', JSON.stringify(updatedApprovals));
      
      console.log('Mock approval rejected successfully');
    } else {
      // API call for real approvals
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_REJECTACTIONFEEDBACK,
        {
          action_id: approval.id,
          manager_id: user.data[0].emp_id,
          feedback: feedback,
        },
        { headers: { Authorization: token } }
      );

      console.log('Task rejected successfully:', response.data);
    }
    
    // Refresh approvals
    fetchApprovals();
    setApprovalDetailsOpen(false);
    setSelectedApproval(null);
    setFeedback('');
    
  } catch (err) {
    console.error('Error rejecting task:', err);
    setError(err.response?.data?.message || 'Failed to reject task');
  } finally {
    setApprovalActionLoading(false);
  }
};


  const handleStartAgent = async (agent) => {
    setActionLoading(prev => ({ ...prev, [agent.id]: true }));
    try {
      console.log('Starting agent:', agent.id);
      
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_STARTANGETHANDLER,
        {
          env: 'prod',
          agent_id: agent.id,
          org_id: user.data[0].client_code
        },
        { headers: { Authorization: token } }
      );

      console.log('Agent started successfully:', response.data);
      
      // Update local state
      setAgents(prev => prev.map(a => 
        a.id === agent.id ? { ...a, status: 'active' } : a
      ));
      
      // Update stats
      setStats(prev => ({
        ...prev,
        active: prev.active + 1,
        stopped: Math.max(0, prev.stopped - 1)
      }));
      
    } catch (err) {
      console.error('Error starting agent:', err);
      setError(err.response?.data?.data || 'Failed to start agent');
    } finally {
      setActionLoading(prev => ({ ...prev, [agent.id]: false }));
    }
  };

  const handleStopAgent = async (agent) => {
    setActionLoading(prev => ({ ...prev, [agent.id]: true }));
    try {
      console.log('Stopping agent:', agent.id);
      
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_STOPANGETHANDLER,
        {
          env: 'prod',
          agent_id: agent.id,
          org_id: user.data[0].client_code
        },
        { headers: { Authorization: token } }
      );

      console.log('Agent stopped successfully:', response.data);
      
      // Update local state
      setAgents(prev => prev.map(a => 
        a.id === agent.id ? { ...a, status: 'stopped' } : a
      ));
      
      // Update stats
      setStats(prev => ({
        ...prev,
        stopped: prev.stopped + 1,
        active: Math.max(0, prev.active - 1)
      }));
      
    } catch (err) {
      console.error('Error stopping agent:', err);
      setError(err.response?.data?.data || 'Failed to stop agent');
    } finally {
      setActionLoading(prev => ({ ...prev, [agent.id]: false }));
    }
  };

  const handleDeleteAgent = async () => {
    if (!selectedAgent) return;
    
    setActionLoading(prev => ({ ...prev, delete: true }));
    try {
      console.log('Deleting agent:', selectedAgent.id);
      
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_DELETEANGETHANDLER,
        {
          env: 'prod',
          agent_id: selectedAgent.id,
          org_id: user.data[0].client_code
        },
        { headers: { Authorization: token } }
      );

      console.log('Agent deleted successfully:', response.data);
      
      // Update local state
      setAgents(prev => prev.filter(a => a.id !== selectedAgent.id));
      setTotalCount(prev => Math.max(0, prev - 1));
      
      // Update stats
      setStats(prev => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
        [selectedAgent.status]: Math.max(0, prev[selectedAgent.status] - 1)
      }));
      
      setDeleteDialogOpen(false);
      setSelectedAgent(null);
      
    } catch (err) {
      console.error('Error deleting agent:', err);
      setError(err.response?.data?.data || 'Failed to delete agent');
    } finally {
      setActionLoading(prev => ({ ...prev, delete: false }));
    }
  };

  const handleViewExecutionAnalysis = async (agent) => {
    setExecutionAgent(agent);
    setExecutionOpen(true);
    setExecutionLoading(true);
    setExecutionError('');

    try {
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_GETANGETHISTORYHANDLER,
        {
          env: 'prod',
          agent_id: agent.id,
          org_id: user.data[0].client_code
        },
        { headers: { Authorization: token } }
      );

      if (response.data?.data) {
        setExecutionData(response.data.data);
      } else {
        setExecutionData(null);
      }
    } catch (err) {
      console.error('Error fetching execution analysis:', err);
      setExecutionError(err.response?.data?.data || 'Failed to load execution analysis');
      
      // Mock data for testing
      setExecutionData({
        last_execution: {
          execution_id: 'exec-123',
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          status: 'success',
          duration: 45,
          trigger: 'scheduled'
        },
        last_discussion: {
          messages: [
            {
              role: 'user',
              content: 'Review the pull request for feature/authentication',
              timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
            },
            {
              role: 'agent',
              content: 'I\'ve analyzed the pull request for feature/authentication. Here are my findings:\n\n1. Code Quality: The implementation follows best practices with proper error handling.\n2. Security: Authentication tokens are properly encrypted.\n3. Testing: Unit tests cover 85% of new code.\n4. Suggestions: Consider adding integration tests for the complete auth flow.',
              timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000 + 30000).toISOString()
            },
            {
              role: 'user',
              content: 'Can you check if the session management is thread-safe?',
              timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000 + 60000).toISOString()
            },
            {
              role: 'agent',
              content: 'Yes, I\'ve reviewed the session management code. It uses thread-safe collections and proper synchronization mechanisms. The implementation correctly handles concurrent session access and prevents race conditions.',
              timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000 + 90000).toISOString()
            }
          ],
          summary: 'Discussed authentication implementation review, covering code quality, security, testing coverage, and thread-safety of session management.'
        },
        performance_metrics: {
          total_executions: 156,
          success_rate: 94.2,
          avg_duration: 38,
          last_30_days: {
            executions: 42,
            success_rate: 95.5
          }
        }
      });
    } finally {
      setExecutionLoading(false);
    }
  };

  const handleCloseExecutionAnalysis = () => {
    setExecutionOpen(false);
    setExecutionAgent(null);
    setExecutionData(null);
    setExecutionError('');
  };

  const handleViewDetails = (agent) => {
    setSelectedAgent(agent);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedAgent(null);
  };

  const handleOpenDelete = (agent) => {
    setSelectedAgent(agent);
    setDeleteDialogOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedAgent(null);
  };

  const handleCreateSuccess = (newAgent) => {
    console.log('New agent created:', newAgent);
    fetchAgents();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

// Line 486 & 492 - Update handleViewApprovalDetails and handleCloseApprovalDetails
const handleViewApprovalDetails = (approval) => {
  setSelectedApproval(approval);
  setApprovalDetailsOpen(true);
  setFeedback('');
};

const handleCloseApprovalDetails = () => {
  setApprovalDetailsOpen(false);
  setSelectedApproval(null);
  setFeedback('');
};

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTriggerLabel = (type) => {
    const labels = {
      inbound_email: 'Email',
      slack_message: 'Slack',
      task_completion: 'Task',
      jira_event: 'Jira',
      github_pr: 'GitHub',
      scheduled: 'Scheduled',
      manual: 'Manual'
    };
    return labels[type] || type;
  };

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !typeFilter || agent.agent_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const filteredApprovals = approvals.filter(approval =>
    approval.agent_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    approval.task_summary.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
<Box sx={{ 
  p: 3, 
  width: `calc(100vw - ${collapsed ? '60px' : '240px'} - 48px)`, 
  marginLeft: collapsed ? '60px' : '240px',
  bgcolor: isDarkMode ? '#121212' : '#f5f5f5',
  minHeight: '100vh'
}}>      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="600">
            Agent Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your AI agents and review pending approvals
          </Typography>
        </Box>
        {mainTab === 0 && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Create Agent
          </Button>
        )}
      </Box>

      {/* Main Tabs */}
  
<Paper sx={{ 
  mb: 3,
  bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
  border: `1px solid ${isDarkMode ? '#333333' : '#e9ecef'}`
}}>
  <Tabs value={mainTab} onChange={(e, v) => setMainTab(v)}>
    <Tab 
      label={
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>Agents</Typography>
          <Chip label={stats.total} size="small" color="primary" />
        </Box>
      } 
    />
    <Tab 
      label={
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>Pending Approvals</Typography>
          {approvalStats.pending > 0 && (
            <Badge badgeContent={approvalStats.pending} color="warning">
              <Box width={20} />
            </Badge>
          )}
        </Box>
      } 
    />
    <Tab 
      label={
        <Box display="flex" alignItems="center" gap={1}>
          <AnalyticsIcon />
          <Typography>Agent Metrics</Typography>
        </Box>
      } 
    />
  </Tabs>
</Paper>

      {/* Agents Tab Content */}
      {mainTab === 0 && (
        <>
          {/* Stats Cards */}
         {/* Stats Cards */}
<Grid container spacing={2} sx={{ mb: 3 }}>
  <Grid item xs={6} sm={3}>
    <Paper sx={{ 
      p: 2,
      bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
      border: `1px solid ${isDarkMode ? '#333333' : '#e9ecef'}`
    }}>
      <Typography color="text.secondary" gutterBottom variant="body2">
        Total Agents
      </Typography>
      <Typography variant="h5" fontWeight="600">
        {stats.total}
      </Typography>
    </Paper>
  </Grid>
  <Grid item xs={6} sm={3}>
    <Paper sx={{ 
      p: 2,
      bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
      border: `1px solid ${isDarkMode ? '#333333' : '#e9ecef'}`
    }}>
      <Typography color="text.secondary" gutterBottom variant="body2">
        Active
      </Typography>
      <Typography variant="h5" fontWeight="600" color="success.main">
        {stats.active}
      </Typography>
    </Paper>
  </Grid>
  <Grid item xs={6} sm={3}>
    <Paper sx={{ 
      p: 2,
      bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
      border: `1px solid ${isDarkMode ? '#333333' : '#e9ecef'}`
    }}>
      <Typography color="text.secondary" gutterBottom variant="body2">
        Stopped
      </Typography>
      <Typography variant="h5" fontWeight="600" color="text.secondary">
        {stats.stopped}
      </Typography>
    </Paper>
  </Grid>
  <Grid item xs={6} sm={3}>
    <Paper sx={{ 
      p: 2,
      bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
      border: `1px solid ${isDarkMode ? '#333333' : '#e9ecef'}`
    }}>
      <Typography color="text.secondary" gutterBottom variant="body2">
        Provisioning
      </Typography>
      <Typography variant="h5" fontWeight="600" color="warning.main">
        {stats.provisioning}
      </Typography>
    </Paper>
  </Grid>
</Grid>
          {/* Filters */}
       {/* Filters */}
<Paper sx={{ 
  p: 2, 
  mb: 2,
  bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
  border: `1px solid ${isDarkMode ? '#333333' : '#e9ecef'}`
}}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search agents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(0);
                    }}
                    label="Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="stopped">Stopped</MenuItem>
                    <MenuItem value="provisioning">Provisioning</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="">All</MenuItem>
                    {Object.entries(AGENT_TYPES).map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={1}>
                <Tooltip title="Refresh">
                  <IconButton onClick={fetchAgents} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Paper>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Agents Table */}
       {/* Agents Table */}
<Paper sx={{
  bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
  border: `1px solid ${isDarkMode ? '#333333' : '#e9ecef'}`
}}>
  <TableContainer>
    <Table>
      <TableHead sx={{ 
        bgcolor: isDarkMode ? '#2c2c2c' : '#f8f9fa'
      }}>
                  <TableRow>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Pod</strong></TableCell>
                    <TableCell><strong>Trigger</strong></TableCell>
                    <TableCell><strong>Created</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          Loading agents...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredAgents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <Typography variant="body1" color="text.secondary">
                          {searchTerm || statusFilter || typeFilter 
                            ? 'No agents match your filters' 
                            : 'No agents created yet'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {!searchTerm && !statusFilter && !typeFilter &&
                            'Click "Create Agent" to get started'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAgents.map((agent) => (
                      <TableRow key={agent.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="500">
                              {agent.name}
                            </Typography>
                            {agent.metadata?.description && (
                              <Typography variant="caption" color="text.secondary">
                                {agent.metadata.description}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={AGENT_TYPES[agent.agent_type] || agent.agent_type}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={AGENT_STATUS[agent.status]?.icon}
                            label={AGENT_STATUS[agent.status]?.label}
                            color={AGENT_STATUS[agent.status]?.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {agent.pod_name || `Pod ${agent.pod_id}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Approver: {agent.approver_name || agent.approver_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getTriggerLabel(agent.trigger_type)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(agent.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            {agent.status === 'stopped' && (
                              <Tooltip title="Start Agent">
                                <IconButton 
                                  size="small" 
                                  color="success"
                                  onClick={() => handleStartAgent(agent)}
                                  disabled={actionLoading[agent.id]}
                                >
                                  {actionLoading[agent.id] ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <StartIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                            {agent.status === 'active' && (
                              <Tooltip title="Stop Agent">
                                <IconButton 
                                  size="small" 
                                  color="warning"
                                  onClick={() => handleStopAgent(agent)}
                                  disabled={actionLoading[agent.id]}
                                >
                                  {actionLoading[agent.id] ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <StopIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small" 
                                onClick={() => handleViewDetails(agent)}
                              >
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Execution Analysis">
                              <IconButton size="small" onClick={() => handleViewExecutionAnalysis(agent)}>
                                <AnalyticsIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleEditAgent(agent)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleOpenDelete(agent)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {!loading && filteredAgents.length > 0 && (
              <>
                <Divider />
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </>
            )}
          </Paper>
        </>
      )}

      {/* Approvals Tab Content */}
      {mainTab === 1 && (
        <>
          {/* Approval Stats */}
        {/* Approval Stats */}
<Grid container spacing={2} sx={{ mb: 3 }}>
  <Grid item xs={12} sm={4}>
    <Paper sx={{ 
      p: 2, 
      bgcolor: isDarkMode ? '#2c2c2c' : 'warning.50',
      border: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}`
    }}>
      <Typography color="text.secondary" gutterBottom variant="body2">
        Pending Approvals
      </Typography>
      <Typography variant="h5" fontWeight="600" color="warning.main">
        {approvalStats.pending}
      </Typography>
    </Paper>
  </Grid>
  <Grid item xs={12} sm={4}>
    <Paper sx={{ 
      p: 2, 
      bgcolor: isDarkMode ? '#2c2c2c' : 'success.50',
      border: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}`
    }}>
      <Typography color="text.secondary" gutterBottom variant="body2">
        Approved
      </Typography>
      <Typography variant="h5" fontWeight="600" color="success.main">
        {approvalStats.approved}
      </Typography>
    </Paper>
  </Grid>
  <Grid item xs={12} sm={4}>
    <Paper sx={{ 
      p: 2, 
      bgcolor: isDarkMode ? '#2c2c2c' : 'error.50',
      border: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}`
    }}>
      <Typography color="text.secondary" gutterBottom variant="body2">
        Retry
      </Typography>
      <Typography variant="h5" fontWeight="600" color="error.main">
        {approvalStats.rejected}
      </Typography>
    </Paper>
  </Grid>
</Grid>

          {/* Approval Filters */}
          <Paper sx={{ mb: 2 }}>
            <Tabs value={approvalStatusFilter} onChange={(e, v) => setApprovalStatusFilter(v)}>
              <Tab value="pending" label={
                <Badge badgeContent={approvalStats.pending} color="warning">
                  Pending
                </Badge>
              } />
              <Tab value="approved" label="Approved" />
              <Tab value="rejected" label="Rejected" />
            </Tabs>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={10}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by agent name or task..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Tooltip title="Refresh">
                  <IconButton onClick={fetchApprovals} disabled={approvalsLoading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Paper>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Approvals Table */}
          {/* Agents Table */}
<Paper sx={{
  bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
  border: `1px solid ${isDarkMode ? '#333333' : '#e9ecef'}`
}}>
  <TableContainer>
    <Table>
      <TableHead sx={{ 
        bgcolor: isDarkMode ? '#2c2c2c' : '#f8f9fa'
      }}>
                
                  <TableRow>
                    <TableCell>Agent</TableCell>
                    <TableCell>Task</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Triggered By</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {approvalsLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          Loading approvals...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredApprovals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                        <Typography variant="body1" color="text.secondary">
                          No {approvalStatusFilter} approvals found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {approvalStatusFilter === 'pending' 
                            ? 'Agents will appear here when they complete work that needs approval'
                            : `No ${approvalStatusFilter} agent work yet`
                          }
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredApprovals.map((approval) => (
                      <TableRow key={approval.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="500">
                              {approval.agent_name}
                            </Typography>
                            <Chip 
                              label={AGENT_TYPES[approval.agent_type] || approval.agent_type}
                              size="small"
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="500">
                              {approval.task_type.replace(/_/g, ' ').toUpperCase()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {approval.task_summary}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={APPROVAL_STATUS[approval.status]?.icon}
                            label={APPROVAL_STATUS[approval.status]?.label}
                            color={APPROVAL_STATUS[approval.status]?.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {approval.triggered_by}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(approval.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small" 
                                onClick={() => handleViewApprovalDetails(approval)}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {approval.status === 'pending' && (
                              <>
                                <Tooltip title="Approve">
                                  <IconButton 
                                    size="small" 
                                    color="success"
                                    onClick={() => handleApproveTask(approval)}
                                  >
                                    <ApproveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Retry">
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleViewApprovalDetails(approval)}
                                  >
                                    <RejectIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
        {/* Agent Metrics Tab Content */}
 {mainTab === 2 && (
        <Box sx={{ mt: -3 }}>
          <AgentPerformanceMetrics collapsed={collapsed} />
        </Box>
      )}
      {/* Create Agent Dialog */}
      <CreateAgent
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Edit Agent Dialog */}
      <CreateAgent
        open={editOpen}
        onClose={handleCloseEdit}
        onSuccess={handleEditSuccess}
        agentToEdit={editAgent}
      />

      {/* Agent Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Agent Details</Typography>
            {selectedAgent && (
              <Chip
                icon={AGENT_STATUS[selectedAgent.status]?.icon}
                label={AGENT_STATUS[selectedAgent.status]?.label}
                color={AGENT_STATUS[selectedAgent.status]?.color}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent dividers sx={{
  bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff'
}}>
          {selectedAgent && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight="500">{selectedAgent.name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Type</Typography>
                <Typography variant="body1">{AGENT_TYPES[selectedAgent.agent_type]}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Pod</Typography>
                <Typography variant="body1">{selectedAgent.pod_name || selectedAgent.pod_id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Approver</Typography>
                <Typography variant="body1">{selectedAgent.approver_name || selectedAgent.approver_id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Trigger Type</Typography>
                <Typography variant="body1">{getTriggerLabel(selectedAgent.trigger_type)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Created</Typography>
                <Typography variant="body1">{formatDate(selectedAgent.created_at)}</Typography>
              </Grid>
              
              {selectedAgent.trigger_config && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Trigger Configuration</Typography>
                  <Paper variant="outlined" sx={{ 
            p: 1, 
            mt: 0.5, 
            bgcolor: isDarkMode ? '#2c2c2c' : 'grey.50',
            border: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}`
          }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {selectedAgent.trigger_config}
                    </Typography>
                  </Paper>
                </Grid>
              )}
              
              {selectedAgent.metadata?.description && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body2">{selectedAgent.metadata.description}</Typography>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Permissions</Typography>
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedAgent.permissions?.map((perm, idx) => (
                    <Chip
                      key={idx}
                      label={`${perm.system}: ${perm.perms.join(', ')}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDelete}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the agent "{selectedAgent?.name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={actionLoading.delete}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAgent}
            color="error"
            variant="contained"
            disabled={actionLoading.delete}
            startIcon={actionLoading.delete ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {actionLoading.delete ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Execution Analysis Dialog */}
      <Dialog open={executionOpen} onClose={handleCloseExecutionAnalysis} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AnalyticsIcon />
            <Typography variant="h6">Execution Analysis - {executionAgent?.name}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {executionLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                Loading execution analysis...
              </Typography>
            </Box>
          ) : executionError ? (
            <Alert severity="error">{executionError}</Alert>
          ) : !executionData ? (
            <Typography>No execution data available.</Typography>
          ) : (
            <Grid container spacing={3}>
              {/* Performance Metrics */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ 
        p: 2, 
        bgcolor: isDarkMode ? '#2c2c2c' : 'primary.50',
        border: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}`
      }}>
                      <Typography variant="caption" color="text.secondary">Total Executions</Typography>
                      <Typography variant="h5" fontWeight="600">
                        {executionData.performance_metrics?.total_executions || 0}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, bgcolor: 'success.50' }}>
                      <Typography variant="caption" color="text.secondary">Success Rate</Typography>
                      <Typography variant="h5" fontWeight="600" color="success.main">
                        {executionData.performance_metrics?.success_rate || 0}%
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, bgcolor: 'info.50' }}>
                      <Typography variant="caption" color="text.secondary">Avg Duration</Typography>
                      <Typography variant="h5" fontWeight="600">
                        {executionData.performance_metrics?.avg_duration || 0}s
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 2, bgcolor: 'warning.50' }}>
                      <Typography variant="caption" color="text.secondary">Last 30 Days</Typography>
                      <Typography variant="h5" fontWeight="600">
                        {executionData.performance_metrics?.last_30_days?.executions || 0}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>

              {/* Last Execution */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Last Execution</Typography>
                {executionData.last_execution ? (
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Execution ID</Typography>
                        <Typography variant="body2" fontFamily="monospace">
                          {executionData.last_execution.execution_id}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Chip
                          label={executionData.last_execution.status}
                          color={executionData.last_execution.status === 'success' ? 'success' : 'error'}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Duration</Typography>
                        <Typography variant="body2">
                          {executionData.last_execution.duration}s
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Timestamp</Typography>
                        <Typography variant="body2">
                          {formatDate(executionData.last_execution.timestamp)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                ) : (
                  <Typography variant="body2" color="text.secondary">No execution data available</Typography>
                )}
              </Grid>

              {/* Last Discussion */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" alignItems="center" gap={1} sx={{ mt: 2, mb: 2 }}>
                  <ChatIcon color="primary" />
                  <Typography variant="h6">Last Discussion</Typography>
                </Box>
                
                {executionData.last_discussion?.summary && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="500">Summary:</Typography>
                    <Typography variant="body2">{executionData.last_discussion.summary}</Typography>
                  </Alert>
                )}

                {executionData.last_discussion?.messages && executionData.last_discussion.messages.length > 0 ? (
                  <Paper sx={{ 
  p: 2, 
  maxHeight: 400, 
  overflow: 'auto', 
  bgcolor: isDarkMode ? '#2c2c2c' : 'grey.50',
  border: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}`
}}>
                    {executionData.last_discussion.messages.map((message, index) => (
                      <Box
                        key={index}
                        sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: message.role === 'user' 
          ? (isDarkMode ? '#1e3a5f' : 'primary.50')
          : (isDarkMode ? '#1e1e1e' : 'background.paper'),
        borderLeft: '4px solid',
        borderColor: message.role === 'user' ? 'primary.main' : 'success.main',
        border: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}`
      }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="caption" fontWeight="600" color={message.role === 'user' ? 'primary.main' : 'success.main'}>
                            {message.role === 'user' ? 'User' : 'Agent'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(message.timestamp)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {message.content}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                ) : (
                  <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <ChatIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No discussion history available
                    </Typography>
                  </Paper>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExecutionAnalysis}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Approval Details Dialog */}
      <Dialog
        open={approvalDetailsOpen}
        onClose={handleCloseApprovalDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Task Approval Details</Typography>
            {selectedApproval && (
              <Chip
                icon={APPROVAL_STATUS[selectedApproval.status]?.icon}
                label={APPROVAL_STATUS[selectedApproval.status]?.label}
                color={APPROVAL_STATUS[selectedApproval.status]?.color}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {selectedApproval && (
            <Box>
              {/* Agent Info */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Action Type</Typography>
                    <Typography variant="body1" fontWeight="500">
                      {selectedApproval.agent_action?.replace(/_/g, ' ')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Chip
                      icon={APPROVAL_STATUS[selectedApproval.status]?.icon}
                      label={APPROVAL_STATUS[selectedApproval.status]?.label}
                      color={APPROVAL_STATUS[selectedApproval.status]?.color}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Manager ID</Typography>
                    <Typography variant="body1">{selectedApproval.manager_id}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography variant="body1">{formatDate(selectedApproval.created_at)}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Task Summary */}
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Task Summary
              </Typography>
              <Typography variant="body2" paragraph>
                {selectedApproval.task_summary}
              </Typography>

              <Divider sx={{ my: 2 }} />

              {/* Entity Data - Formatted */}
             {/* Entity Data - Formatted */}
<Typography variant="subtitle1" fontWeight="600" gutterBottom>
  Detailed Information
</Typography>
<Paper variant="outlined" sx={{ 
  p: 2, 
  bgcolor: isDarkMode ? '#2c2c2c' : 'grey.50',
  maxHeight: 400, 
  overflow: 'auto',
  border: `1px solid ${isDarkMode ? '#404040' : '#e9ecef'}`
}}>
  <Typography variant="body2" sx={{ 
    whiteSpace: 'pre-wrap', 
    fontFamily: 'monospace', 
    fontSize: '12px',
    color: isDarkMode ? '#b0b0b0' : 'text.primary'
  }}>
    {selectedApproval.entity_data?.replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/^"|"$/g, '')}
  </Typography>
</Paper>
              {/* Approval/Rejection Info */}
              {selectedApproval.approved_by && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Alert severity="success">
                    <Typography variant="subtitle2" gutterBottom>Approved By:</Typography>
                    <Typography variant="body2">Manager ID: {selectedApproval.approved_by}</Typography>
                    {selectedApproval.executed_at && (
                      <Typography variant="caption" display="block">
                        Executed: {formatDate(selectedApproval.executed_at)}
                      </Typography>
                    )}
                  </Alert>
                </>
              )}

              {selectedApproval.feedback_comment && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Alert severity="error">
                    <Typography variant="subtitle2" gutterBottom>Rejection Reason:</Typography>
                    <Typography variant="body2">{selectedApproval.feedback_comment}</Typography>
                  </Alert>
                </>
              )}

              {/* Jira Output */}
             {/* Jira Output */}
{selectedApproval.jira_output && (
  <>
    <Divider sx={{ my: 2 }} />
    <Typography variant="subtitle1" fontWeight="600" gutterBottom>
      Jira Output
    </Typography>
    <Paper variant="outlined" sx={{ 
      p: 2, 
      bgcolor: isDarkMode ? '#1e3a2f' : 'success.50',
      border: `1px solid ${isDarkMode ? '#2d5a45' : '#e9ecef'}`
    }}>
      <Typography variant="body2" sx={{ 
        whiteSpace: 'pre-wrap', 
        fontFamily: 'monospace', 
        fontSize: '12px',
        color: isDarkMode ? '#b0b0b0' : 'text.primary'
      }}>
        {selectedApproval.jira_output}
      </Typography>
    </Paper>
  </>
)}
              {/* Reject Reason Input (for pending items) */}
           
{/* Feedback Input (for pending items) */}
{selectedApproval.status === 'pending' && (
  <>
    <Divider sx={{ my: 2 }} />
    <TextField
      fullWidth
      required
      label="Feedback (Required)"
      placeholder="Provide your feedback for this action (required for both approve and reject)..."
      value={feedback}
      onChange={(e) => setFeedback(e.target.value)}
      multiline
      rows={3}
      disabled={approvalActionLoading}
      error={!feedback.trim() && error.includes('feedback')}
      helperText={!feedback.trim() && error.includes('feedback') ? 'Feedback is required' : ''}
    />
  </>
)}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseApprovalDetails} disabled={approvalActionLoading}>
            Close
          </Button>
          {selectedApproval?.status === 'pending' && (
            <>
              <Button
                onClick={() => handleRejectTask(selectedApproval)}
                variant="outlined"
                color="error"
                disabled={approvalActionLoading}
                startIcon={approvalActionLoading ? <CircularProgress size={20} /> : <RejectIcon />}
              >
                Retry
              </Button>
              <Button
                onClick={() => handleApproveTask(selectedApproval)}
                variant="contained"
                color="success"
                disabled={approvalActionLoading}
                startIcon={approvalActionLoading ? <CircularProgress size={20} /> : <ApproveIcon />}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentsList;